import * as React from "react";
import {useContext, useEffect, useMemo, useState} from "react";
import {Button, Dropdown, Form, Grid, Icon, Input, Segment} from "semantic-ui-react";
import {ConfigContext} from "../../../context/ConfigContext";
import {backend} from "../../../../../xconvert-backend";
import {authentication} from "../../../../../authentication";
import {TimedBarChart} from "../../systemStatus/EtaQuality/TimedBarChart";
import {TimedLineChart} from "../../systemStatus/EtaQuality/TimedLineChart";
import {notify} from "react-notify-toast";
import {CustomTableComponent, FetchResult, TableDataSource} from "../../../../ui/customTable/customTableComponent";
import {FetchHistoryRequest} from "../../../../../generated";
import SortDirectionEnum = FetchHistoryRequest.SortDirectionEnum;

export interface ReportStatsPageProps {
}

export interface ReportStatsPageState {
    stats: any[];
    isLoading: boolean;
    page: number;
    take: number;
    sortBy: string;
    sortDirection: "ASC" | "DESC";
    timeRange: "30d" | "90d" | "6m" | "1y" | "all";
    filters: {
        companyId?: string;
        reportName?: string;
        generatedBy?: string;
        generationType?: "MANUAL" | "AUTO";
    };
}

export function ReportStatsPage(props: React.PropsWithChildren<ReportStatsPageProps>) {
    const context = useContext(ConfigContext);
    
    if (!authentication.isSuperUser) {
        return (
            <Segment>
                <h2>Access Denied</h2>
                <p>This page is only available for super users.</p>
            </Segment>
        );
    }

    const [state, setState] = useState<ReportStatsPageState>({
        stats: [],
        isLoading: false,
        page: 0,
        take: 50,
        sortBy: "GENERATION_TIME",
        sortDirection: "DESC",
        timeRange: "30d",
        filters: {}
    });

    useEffect(() => {
        fetchData();
    }, [state.sortBy, state.sortDirection, state.timeRange]);

    async function fetchData(options?: { append?: boolean }) {
        const append = options?.append === true;
        setState(prev => ({...prev, isLoading: true}));
        try {
            const auth = backend.withTokenAuthHeader(authentication.token);
            const params = new URLSearchParams();
            params.append("skip", "0");
            params.append("limit", "1000");
            params.append("sortBy", state.sortBy);
            params.append("sortDirection", state.sortDirection);

            const now = new Date();
            let startTime: Date | null = null;
            if (state.timeRange === "30d") {
                startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            } else if (state.timeRange === "90d") {
                startTime = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            } else if (state.timeRange === "6m") {
                const d = new Date(now);
                d.setMonth(d.getMonth() - 6);
                startTime = d;
            } else if (state.timeRange === "1y") {
                const d = new Date(now);
                d.setFullYear(d.getFullYear() - 1);
                startTime = d;
            } else {
                startTime = null;
            }

            if (startTime) {
                params.append("startTime", startTime.toISOString());
            }
            if (state.timeRange !== "all") {
                params.append("endTime", now.toISOString());
            }

            if (append) {
                const lastId = state.stats.reduce((maxId: string, s: any) => {
                    const currentId = String(s._id || "").trim();
                    if (!currentId) {
                        return maxId;
                    }
                    return currentId > maxId ? currentId : maxId;
                }, "");
                if (lastId) {
                    params.append("sinceId", lastId);
                }
            }

            const response = await fetch(`/api/internal/reportGenerationStats?${params.toString()}`, {
                headers: {
                    ...auth.headers,
                    'Authorization': String(auth.headers['Authorization']),
                    'x-company-id': String(auth.headers['x-company-id']),
                    'x-request-id': String(auth.headers['x-request-id']),
                    ...(auth.headers['Accept'] ? { 'Accept': String(auth.headers['Accept']) } : {})
                } as Record<string, string>
            });

            if (response.ok) {
                const data = await response.json();
                setState(prev => {
                    const newStats: any[] = data.stats || [];
                    if (!append) {
                        return {
                            ...prev,
                            stats: newStats,
                            isLoading: false
                        };
                    }
                    const existingIds = new Set<string>(prev.stats.map((s: any) => String(s._id || "")));
                    const merged = [
                        ...prev.stats,
                        ...newStats.filter((s: any) => !existingIds.has(String(s._id || "")))
                    ];
                    return {
                        ...prev,
                        stats: merged,
                        isLoading: false
                    };
                });
            } else {
                console.error("Failed to fetch report stats:", response.statusText);
                setState(prev => ({...prev, isLoading: false}));
            }
        } catch (error) {
            console.error("Error fetching report stats:", error);
            setState(prev => ({...prev, isLoading: false}));
        }
    }

    function changeSort(sortField: string) {
        setState(prev => {
            const newDirection = prev.sortBy === sortField && prev.sortDirection === "DESC" ? "ASC" : "DESC";
            return {
                ...prev,
                sortBy: sortField,
                sortDirection: newDirection,
                page: 0
            };
        });
    }

    function formatDate(dateString: string) {
        if (!dateString) {
            return "";
        }
        try {
            const date = new Date(dateString);
            return date.toLocaleString();
        } catch (e) {
            return dateString;
        }
    }

    function handleCopyCompanyId(companyId: string) {
        if (!companyId) {
            return;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(companyId)
                .then(() => {
                    notify.show("Company ID copied to clipboard", "success", 1500);
                })
                .catch(err => {
                    console.error("Failed to copy companyId to clipboard", err);
                    notify.show("Failed to copy company ID", "error", 3000);
                });
        } else {
            console.log("Company ID:", companyId);
            notify.show("Company ID logged to console", "info", 2000);
        }
    }

    const filteredStats = useMemo(() => {
        return state.stats.filter((s: any) => {
            if (state.filters.companyId && !String(s.companyId || "").includes(state.filters.companyId)) {
                return false;
            }
            if (state.filters.reportName && !String(s.reportName || "").toLowerCase().includes(state.filters.reportName.toLowerCase())) {
                return false;
            }
            if (state.filters.generatedBy && !String(s.generatedBy || "").toLowerCase().includes(state.filters.generatedBy.toLowerCase())) {
                return false;
            }
            if (state.filters.generationType && s.generationType !== state.filters.generationType) {
                return false;
            }
            return true;
        });
    }, [state.stats, state.filters]);

    const summaryData = useMemo(() => {
        if (state.filters.companyId) {
            const countsByReportType: Record<string, number> = {};
            filteredStats.forEach((s: any) => {
                const typeKey = String(s.reportType || s.reportName || "UNKNOWN");
                countsByReportType[typeKey] = (countsByReportType[typeKey] || 0) + 1;
            });

            return Object.entries(countsByReportType)
                .sort(([, a], [, b]) => b - a)
                .map(([name, value]) => ({
                    name,
                    value
                }));
        }

        const countsByCompany: Record<string, { companyId: string; companyName: string; count: number }> = {};
        filteredStats.forEach((s: any) => {
            const companyId = String(s.companyId || "").trim();
            if (!companyId) {
                return;
            }
            const companyName = String(s.companyName || companyId);
            const existing = countsByCompany[companyId];
            if (existing) {
                existing.count += 1;
            } else {
                countsByCompany[companyId] = {
                    companyId,
                    companyName,
                    count: 1
                };
            }
        });

        return Object.values(countsByCompany)
            .sort((a, b) => b.count - a.count)
            .map(entry => {
                const firstWord = entry.companyName.split(" ")[0] || entry.companyId;
                return {
                    name: firstWord,
                    value: entry.count,
                    companyId: entry.companyId,
                    fullCompanyName: entry.companyName
                };
            });
    }, [filteredStats, state.filters.companyId]);

    const userSummaryData = useMemo(() => {
        const countsByUser: Record<string, number> = {};
        filteredStats.forEach((s: any) => {
            const userKey = String(s.generatedBy || "UNKNOWN");
            countsByUser[userKey] = (countsByUser[userKey] || 0) + 1;
        });

        return Object.entries(countsByUser)
            .sort(([, a], [, b]) => b - a)
            .map(([name, value]) => ({name, value}));
    }, [filteredStats]);

    const selectedCompanyName = useMemo(() => {
        if (!state.filters.companyId) {
            return null;
        }
        const firstMatch = filteredStats.find((s: any) => 
            String(s.companyId || "").trim() === state.filters.companyId?.trim()
        );
        return firstMatch?.companyName || state.filters.companyId;
    }, [filteredStats, state.filters.companyId]);

    const timeSeriesData = useMemo(() => {
        if (!state.filters.companyId) {
            return [];
        }
        const countsByMonth: Record<string, number> = {};
        filteredStats.forEach((s: any) => {
            if (!s.generationTime) {
                return;
            }
            const d = new Date(s.generationTime);
            if (Number.isNaN(d.getTime())) {
                return;
            }
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            countsByMonth[key] = (countsByMonth[key] || 0) + 1;
        });

        return Object.entries(countsByMonth)
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
            .map(([name, value]) => ({name, value}));
    }, [filteredStats, state.filters.companyId]);

    const columns = useMemo(() => [
        {
            id: 'generationTime',
            Header: 'Generation Time',
            accessor: (d: any) => formatDate(d.generationTime),
            width: 180,
            sortField: 'GENERATION_TIME'
        },
        {
            id: 'companyId',
            Header: 'Company ID',
            accessor: (d: any) => d.companyId || "-",
            width: 240,
            Cell: (row: any) => {
                const id = row.original?.companyId || "";
                return (
                    <span>
                        {id || "-"}
                        {id && (
                            <Icon
                                name="copy"
                                link
                                style={{marginLeft: 8}}
                                title="Copy company ID"
                                onClick={() => handleCopyCompanyId(id)}
                            />
                        )}
                    </span>
                );
            }
        },
        {
            id: 'companyName',
            Header: 'Company',
            accessor: (d: any) => d.companyName || d.companyId,
            width: 200,
            sortField: 'COMPANY_NAME'
        },
        {
            id: 'reportName',
            Header: 'Report Name',
            accessor: (d: any) => d.reportName,
            width: 200,
            sortField: 'REPORT_NAME'
        },
        {
            id: 'generationType',
            Header: 'Type',
            accessor: (d: any) => d.generationType,
            width: 100
        },
        {
            id: 'generatedBy',
            Header: 'Generated By',
            accessor: (d: any) => d.generatedBy || "N/A",
            width: 200
        },
        {
            id: 'sentByEmail',
            Header: 'Sent by Email',
            accessor: (d: any) => d.sentByEmail ? "Yes" : "No",
            width: 120
        },
        {
            id: 'emailReceivers',
            Header: 'Email Receivers',
            accessor: (d: any) => d.emailReceivers?.join(", ") || "-",
            width: 250
        },
        {
            id: 'uploadedToFtp',
            Header: 'Uploaded to FTP',
            accessor: (d: any) => d.uploadedToFtp ? "Yes" : "No",
            width: 130
        }
    ], []);

    const tableFetcher: TableDataSource<any> = useMemo(() => ({
        resetPagination: false,
        fetchData: async (currentPage: number, numRowsToTake: number, sortColumn?: string, sortDirection?: SortDirectionEnum): Promise<FetchResult<any>> => {
            if (sortColumn) {
                const sortField = columns.find((col: any) => col.id === sortColumn)?.sortField || sortColumn.toUpperCase();
                if (sortField !== state.sortBy) {
                    const newDirection = sortDirection === SortDirectionEnum.DESC ? "DESC" : "ASC";
                    changeSort(sortField);
                }
            }
            const startIndex = currentPage * numRowsToTake;
            const endIndex = startIndex + numRowsToTake;
            const pageData = filteredStats.slice(startIndex, endIndex);
            return {
                totalRecords: filteredStats.length,
                data: pageData
            };
        }
    }), [filteredStats, state.sortBy, columns]);

    function drawTable() {
        return (
            <CustomTableComponent
                fetcher={tableFetcher}
                cols={columns}
                name="reportStats"
                formatSortByEnumValue={(sortByColumn: string) => {
                    const column = columns.find((col: any) => col.id === sortByColumn);
                    return column?.sortField || sortByColumn.toUpperCase();
                }}
                style={{cursor: "pointer"}}
            />
        );
    }

    function handleFilterChange(key: string, value: any) {
        setState(prev => ({
            ...prev,
            filters: {
                ...prev.filters,
                [key]: value || undefined
            },
            page: 0
        }));
    }

    function handleTimeRangeChange(value: any) {
        const v = (value || "30d") as ReportStatsPageState["timeRange"];
        setState(prev => ({
            ...prev,
            timeRange: v,
            page: 0
        }));
        fetchData({append: false});
    }

    function handleUserBarClick(payload: any) {
        const generatedBy = String(payload?.name || "").trim();
        if (!generatedBy || generatedBy === "UNKNOWN") {
            return;
        }
        setState(prev => ({
            ...prev,
            filters: {
                ...prev.filters,
                generatedBy
            },
            page: 0
        }));
    }

    function handleCompanyBarClick(payload: any) {
        const companyId = String(payload?.companyId || "").trim();
        if (!companyId) {
            return;
        }
        setState(prev => ({
            ...prev,
            filters: {
                ...prev.filters,
                companyId
            },
            page: 0
        }));
    }

    function clearFilters() {
        setState(prev => ({
            ...prev,
            filters: {},
            page: 0
        }));
    }

    return (
        <Segment>
            <h2>Report Generation Statistics</h2>
            <p>Overview of report usage by companies - which reports are generated manually vs automatically, and whether they are sent by email.</p>

            <Form style={{marginBottom: "20px"}}>
                <Form.Group widths="equal">
                    <Form.Field>
                        <label>Company ID</label>
                        <Input
                            placeholder="Filter by company ID"
                            value={state.filters.companyId || ""}
                            onChange={(e, {value}) => handleFilterChange("companyId", value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Report Name</label>
                        <Input
                            placeholder="Filter by report name"
                            value={state.filters.reportName || ""}
                            onChange={(e, {value}) => handleFilterChange("reportName", value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Generated By</label>
                        <Input
                            placeholder="Filter by user (email)"
                            value={state.filters.generatedBy || ""}
                            onChange={(e, {value}) => handleFilterChange("generatedBy", value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Generation Type</label>
                        <Dropdown
                            placeholder="All"
                            selection
                            options={[
                                {key: "all", text: "All", value: null},
                                {key: "manual", text: "Manual", value: "MANUAL"},
                                {key: "auto", text: "Automatic", value: "AUTO"}
                            ]}
                            value={state.filters.generationType || null}
                            onChange={(e, {value}) => handleFilterChange("generationType", value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Time Range</label>
                        <Dropdown
                            placeholder="Last 30 days"
                            selection
                            options={[
                                {key: "30d", text: "Last 30 days", value: "30d"},
                                {key: "90d", text: "Last 90 days", value: "90d"},
                                {key: "6m", text: "Last 6 months", value: "6m"},
                                {key: "1y", text: "Last 1 year", value: "1y"},
                                {key: "all", text: "All time", value: "all"}
                            ]}
                            value={state.timeRange}
                            onChange={(e, {value}) => handleTimeRangeChange(value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>&nbsp;</label>
                        <Button.Group>
                            <Button onClick={clearFilters}>Clear Filters</Button>
                            <Button
                                icon="refresh"
                                content="Refresh Data"
                                onClick={() => fetchData({append: true})}
                            />
                        </Button.Group>
                    </Form.Field>
                </Form.Group>
            </Form>

            {state.filters.companyId ? (
                <Segment>
                    <Grid stackable columns={3}>
                        <Grid.Row>
                            <Grid.Column>
                                <h3>Report types for {selectedCompanyName || state.filters.companyId}</h3>
                                <TimedBarChart
                                    data={summaryData}
                                    loading={state.isLoading}
                                />
                            </Grid.Column>
                            <Grid.Column>
                                <h3>Executions by user ({userSummaryData.length} users)</h3>
                                <TimedBarChart
                                    data={userSummaryData}
                                    loading={state.isLoading}
                                    onBarClick={handleUserBarClick}
                                />
                            </Grid.Column>
                            <Grid.Column>
                                <h3>Monthly executions for {selectedCompanyName || state.filters.companyId}</h3>
                                <TimedLineChart data={timeSeriesData} loading={state.isLoading}/>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            ) : (
                <Segment>
                    <h3>All companies by report executions ({summaryData.length} companies)</h3>
                    <div style={{ overflowY: "auto", maxHeight: "600px", width: "100%" }}>
                        <TimedBarChart
                            data={summaryData}
                            loading={state.isLoading}
                            onBarClick={handleCompanyBarClick}
                            height={Math.max(400, summaryData.length * 30)}
                        />
                    </div>
                </Segment>
            )}

            {drawTable()}
        </Segment>
    );
}

